'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { AnimatePresence } from 'framer-motion';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

import MenuIcon from '@/assets/icons/MenuIcon';
import { topBarData } from '@/components/layout/Header/headerTopBar';
import {
  type DarkZone,
  isElementOverDark,
  useHeaderBehavior,
} from '@/components/layout/Header/useHeaderBehavior';
import { useMegaMenuData } from '@/components/layout/Header/useMegaMenuData';
import PrimaryButton from '@/components/ui/Button/PrimaryButton';
import R2Image from '@/components/ui/R2Image';
import { useBookingFlowStore } from '@/features/booking/store/useBookingFlowStore';
import { usePharmacyBookingModal } from '@/features/pharamacy/components/PharmacyBookingModalContext/PharmacyBookingModalContext';

const MobileMenu = dynamic(
  () => import('@/components/layout/Header/MobileMenu'),
  { ssr: false },
);

const DropdownPanel = dynamic(
  () => import('@/components/layout/Header/DropdownPanel'),
  { ssr: false },
);

const TEXT_LIGHT = '#2c3480';
const TEXT_DARK = '#d0d0d0';

const BOOKING_WIDGET_TARGET_ID = 'booking-calendar-widget';

const BOOKING_FALLBACK_PATH = '/specialities?filter=all';
const BOOKING_SCROLL_OFFSET = 112;

function escapeSelectorValue(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }

  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function findTargetByIdOrTestId(id: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;

  return (
    document.getElementById(id) ??
    document.querySelector<HTMLElement>(
      `[data-testid="${escapeSelectorValue(id)}"]`,
    )
  );
}

function findBookingWidgetTarget(): HTMLElement | null {
  return findTargetByIdOrTestId(BOOKING_WIDGET_TARGET_ID);
}

function scrollToTarget(target: HTMLElement): void {
  const top =
    target.getBoundingClientRect().top + window.scrollY - BOOKING_SCROLL_OFFSET;

  window.scrollTo({
    top,
    behavior: 'smooth',
  });

  const hadTabIndex = target.hasAttribute('tabindex');

  if (!hadTabIndex) {
    target.setAttribute('tabindex', '-1');
  }

  window.setTimeout(() => {
    target.focus({ preventScroll: true });
  }, 350);
}

function useElementColor(darkZones: DarkZone[]): {
  ref: React.RefObject<HTMLElement | null>;
  color: string;
} {
  const ref = useRef<HTMLElement>(null);
  const [color, setColor] = useState(TEXT_LIGHT);

  useEffect(() => {
    if (!ref.current) return;

    const r = ref.current.getBoundingClientRect();
    const over = isElementOverDark(r.left, r.right, darkZones);

    setColor(over ? TEXT_DARK : TEXT_LIGHT);
  }, [darkZones]);

  return { ref, color };
}

const NavButton: React.FC<{
  label: string;
  isActive: boolean;
  darkZones: DarkZone[];
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}> = ({ label, isActive, darkZones, onMouseEnter, onMouseLeave }) => {
  // eslint-disable-next-line unused-imports/no-unused-vars
  const { ref, color } = useElementColor(darkZones);

  return (
    <li
      ref={ref as React.RefObject<HTMLLIElement>}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button
        className="font-mulish text-theme-primary-dark flex cursor-pointer items-center gap-1 text-base leading-5.5 font-bold transition-colors"
        style={{
          textShadow: isActive ? '0 2px 8px rgba(44, 122, 58, 0.4)' : undefined,
          // color,
        }}
        type="button"
      >
        {label}
        {isActive ? (
          <svg width={10} height={6} viewBox="0 0 10 6" fill="currentColor">
            <polygon points="5,0 10,6 0,6" />
          </svg>
        ) : (
          <svg width={10} height={6} viewBox="0 0 10 6" fill="currentColor">
            <polygon points="0,0 10,0 5,6" />
          </svg>
        )}
      </button>
    </li>
  );
};

export const Header = ({
  isOverlay,
  isLandingPage: _isLandingPage,
  email,
  phone,
  btnTitle,
}: {
  isOverlay?: boolean;
  isLandingPage?: boolean;
  email?: string;
  phone?: string;
  btnTitle?: string;
}) => {
  const pathname = usePathname();
  const router = useRouter();
  const isPharmacyPage = pathname === '/pharmacy';

  const isWidgetOpen = useBookingFlowStore((state) => state.isWidgetOpen);
  const { openModal: openPharmacyModal } = usePharmacyBookingModal();

  const { navItems: NAV_ITEMS, specialistsBySpeciality } = useMegaMenuData();

  const [hoveredSpecialitySlug, setHoveredSpecialitySlug] = useState<
    string | null
  >(null);

  const {
    activeMenu,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    mounted,
    headerRect,
    headerRef,
    dropdownRef,
    handleMouseEnter,
    handleMouseLeave,
    isScrolling,
    darkZones,
  } = useHeaderBehavior();

  const activeItem = NAV_ITEMS.find((item) => item.id === activeMenu);

  // ── Hover-driven specialist filtering for "Our Specialists" tab ──────────
  // When the "Our Specialists" dropdown is open, find the "Browse by Speciality"
  // column and use its first item's slug as the initial selection. On hover,
  // swap the Featured Specialists column to show doctors from that speciality.
  const isSpecialistsTab = activeItem?.id === 'our-specialists';

  const specialitiesColumn = isSpecialistsTab
    ? activeItem?.columns?.find((c) => c.dynamicSource === 'specialities')
    : undefined;

  // Auto-select the first speciality when the tab opens and no hover has occurred
  const firstSpecialitySlug =
    specialitiesColumn?.items?.[0]?.specialitySlug ?? null;

  const effectiveSpecialitySlug = hoveredSpecialitySlug ?? firstSpecialitySlug;

  const columns = activeItem?.columns;

  // Build filtered columns: replace the specialists column's consultants
  const effectiveColumns = useMemo(() => {
    if (!isSpecialistsTab || !columns || !effectiveSpecialitySlug) {
      return columns;
    }

    return columns.map((col) => {
      if (col.dynamicSource !== 'specialists') return col;

      const filtered =
        specialistsBySpeciality.get(effectiveSpecialitySlug) ?? [];

      if (filtered.length === 0 && hoveredSpecialitySlug) {
        // Explicitly hovered item has no doctors — return empty list instead of unrelated default doctors
        return {
          ...col,
          consultants: [],
        };
      }

      if (filtered.length === 0) {
        // No doctors for initial default selection — keep original consultants
        return col;
      }

      return {
        ...col,
        consultants: filtered.slice(0, col.dynamicLimit ?? 4),
      };
    });
  }, [
    isSpecialistsTab,
    columns,
    effectiveSpecialitySlug,
    specialistsBySpeciality,
    hoveredSpecialitySlug,
  ]);

  // Reset hover when switching away from specialists tab
  useEffect(() => {
    if (!isSpecialistsTab) {
      setHoveredSpecialitySlug(null);
    }
  }, [isSpecialistsTab]);

  const { ref: topBarRef, color: topBarColor } = useElementColor(darkZones);
  const { ref: menuBtnRef, color: menuBtnColor } = useElementColor(darkZones);

  const handleBookAppointmentClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      event.preventDefault();

      if (isPharmacyPage) {
        openPharmacyModal();
        return;
      }

      const bookingWidgetTarget = findBookingWidgetTarget();

      if (bookingWidgetTarget) {
        scrollToTarget(bookingWidgetTarget);
        return;
      }

      router.push(BOOKING_FALLBACK_PATH);
    },
    [isPharmacyPage, openPharmacyModal, router],
  );

  if (isWidgetOpen) {
    return null;
  }

  return (
    <>
      <header
        ref={headerRef}
        className={`bg-surface-header-glass fixed top-8 right-0 left-0 z-100 mx-auto w-[calc(100%-2rem)] rounded-2xl border border-white/30 shadow-[0_8px_32px_rgba(0,0,0,0.18)] backdrop-blur-md transition-transform duration-300 xl:max-w-[1233px] xl:rounded-[8px] ${
          isScrolling ? 'translate-y-[-200%]' : 'translate-y-0'
        } xl:pr-p-sm xl:pb-p-sm xl:pl-p-sm xl:flex xl:h-[102px] xl:flex-col xl:justify-between xl:gap-[8px] xl:pt-[8px]`}
      >
        <div className="pointer-events-none absolute inset-0 z-[-1] rounded-2xl xl:rounded-[8px]" />

        <div className="xl:h-p-lg relative z-30 flex items-center rounded-t-2xl py-2 xl:rounded-t-[8px] xl:py-0">
          <div className="flex w-full justify-end px-6 xl:px-0">
            <span
              ref={topBarRef as React.RefObject<HTMLSpanElement>}
              className="text-xs font-medium transition-colors"
              style={{ color: topBarColor }}
              suppressHydrationWarning
            >
              <span className="group relative z-40 flex gap-2 font-bold">
                <a
                  href={
                    _isLandingPage
                      ? `mailto:${email}`
                      : '/about-lips/health-blogs'
                  }
                  target={_isLandingPage ? undefined : '_blank'}
                  rel={_isLandingPage ? undefined : 'noopener noreferrer'}
                  className="relative inline-flex items-center gap-1 transition-colors hover:cursor-pointer"
                  aria-label={
                    _isLandingPage ? `Email ${email}` : 'Visit our blogs'
                  }
                >
                  <span aria-hidden="true">
                    {_isLandingPage ? email : topBarData.cliniciansBar.blogs}
                  </span>
                </a>
                <span aria-hidden="true" className="text-xs text-slate-400">
                  |
                </span>
                <a
                  href={`tel:${(phone ? phone : topBarData.cliniciansBar.phone).replace(/\s+/g, '')}`}
                  className="relative inline-flex items-center gap-1 transition-colors hover:cursor-pointer"
                  aria-label={`Call us at ${phone ? phone : topBarData.cliniciansBar.phone}`}
                >
                  <span aria-hidden="true">
                    {phone ? phone : topBarData.cliniciansBar.phone}
                  </span>
                </a>
              </span>
            </span>
          </div>
        </div>

        <hr className="relative z-10 mx-4 border border-slate-300 xl:mx-0" />

        <nav className="relative z-20 flex items-center justify-between px-6 py-4 xl:h-[50px] xl:px-0 xl:py-0">
          <Link
            href={!_isLandingPage ? '/' : ''}
            className="flex shrink-0 items-center"
          >
            <R2Image
              path="/images/logo/lips-logo.png"
              alt="LIPS Healthcare"
              width={120}
              height={40}
              className="h-10 w-auto"
            />
          </Link>

          <ul className="hidden flex-1 items-center justify-center gap-8 lg:flex">
            {!_isLandingPage &&
              NAV_ITEMS.map((item) => (
                <NavButton
                  key={item.id}
                  label={item.label}
                  isActive={activeMenu === item.id}
                  darkZones={darkZones}
                  onMouseEnter={() => handleMouseEnter(item.id)}
                  onMouseLeave={handleMouseLeave}
                />
              ))}
          </ul>

          <div className="relative z-10 flex shrink-0 items-center gap-4">
            <div className="hidden sm:block" data-testid="header-book-cta">
              <PrimaryButton
                data={{ title: btnTitle || 'Book an appointment' }}
                widthFitContent
                onClick={handleBookAppointmentClick}
              />
            </div>

            <button
              ref={menuBtnRef as React.RefObject<HTMLButtonElement>}
              onClick={() => setIsMobileMenuOpen(true)}
              className="-mr-2 rounded-lg p-2 transition-colors hover:bg-white/20 lg:hidden"
              style={{ color: menuBtnColor }}
              aria-label="Open menu"
              type="button"
            >
              <MenuIcon width={24} height={24} />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {mounted && isMobileMenuOpen && (
          <MobileMenu
            onClose={() => setIsMobileMenuOpen(false)}
            items={NAV_ITEMS}
          />
        )}
      </AnimatePresence>

      {mounted &&
        headerRect &&
        activeItem?.columns &&
        createPortal(
          <div
            ref={dropdownRef}
            data-testid="mega-menu"
            onMouseEnter={() => handleMouseEnter(activeItem.id)}
            onMouseLeave={handleMouseLeave}
            className="animate-in fade-in slide-in-from-top-2 bg-theme-glass-white fixed rounded-2xl border border-white/30 backdrop-blur-md duration-300"
            style={{
              top: headerRect.bottom + 16,
              left: headerRect.left,
              width: headerRect.width,
              zIndex: 9999,
              boxShadow: '0px 4px 4px 0px #4DCFB840',
              ...(isSpecialistsTab ? { minHeight: '420px' } : {}),
            }}
          >
            <div className="p-6">
              <DropdownPanel
                columns={effectiveColumns ?? activeItem.columns}
                featuredCard={activeItem.featuredCard ?? undefined}
                onSpecialityHover={
                  isSpecialistsTab ? setHoveredSpecialitySlug : undefined
                }
                activeSpecialitySlug={
                  isSpecialistsTab
                    ? (effectiveSpecialitySlug ?? undefined)
                    : undefined
                }
              />
            </div>
          </div>,
          document.body,
        )}

      {!isOverlay && <div className="h-34.25 w-full xl:h-[102px]" />}
    </>
  );
};

export default Header;
